import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import OrderForm from './OrderForm';
import OrderHistory from './OrderHistory';
import OrderTracking from './OrderTracking';
import PaymentStatus from './PaymentStatus';
import { orderService } from '../../services/api';

interface Order {
  id: string;
  certificateType: string;
  propertyId: string;
  propertyAddress?: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  partnerId?: string;
  partnerName?: string;
  estimatedCompletionDate?: string;
  notes?: string;
  totalAmount?: number;
  paymentStatus?: string;
  dateRange?: { startDate: string; endDate: string };
  accessDetails?: { contactName: string; contactPhone: string; accessInstructions: string; accessMethod: string };
}

export default function CertificateOrdering() {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  // Load orders on mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await orderService.listOrders();
      if (res.data?.data) {
        setOrders(res.data.data);
        if (res.data.data.length > 0 && !selectedOrder) {
          setSelectedOrder(res.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderCreated = (newOrder: Order) => {
    setOrders([newOrder, ...orders]);
    setSelectedOrder(newOrder);
    navigate(`/certificate-orders/${newOrder.id}`);
  };

  const handleOrderUpdate = (updatedOrder: Order) => {
    setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
    setSelectedOrder(updatedOrder);
  };

  // Determine active tab based on current path
  const isNewOrder = location.pathname.includes('/new');
  const isOrderDetail = location.pathname.match(/\/certificate-orders\/[^/]+/);
  const isPayment = location.pathname.includes('/payment');
  const isHistory = location.pathname === '/certificate-orders' ||
                    (location.pathname.includes('/certificate-orders') && !isNewOrder && !isOrderDetail);

  const orderId = location.pathname.split('/')[2];
  const currentOrder = orders.find(o => o.id === orderId) || selectedOrder;

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col font-sans">
      <Navbar />

      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Tab Navigation - Mobile Responsive */}
        {!isOrderDetail && !isPayment && (
          <div className="mb-6 border-b border-gray-200 -mx-4 px-4 sm:mx-0 sm:px-0">
            <nav className="flex gap-6 sm:gap-8 overflow-x-auto pb-4 scrollbar-hide">
              <button
                onClick={() => navigate('/certificate-orders')}
                className={`py-2 font-medium text-sm whitespace-nowrap transition border-b-2 ${
                  isHistory
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                📋 My Orders
              </button>
              <button
                onClick={() => navigate('/certificate-orders/new')}
                className={`py-2 font-medium text-sm whitespace-nowrap transition border-b-2 ${
                  isNewOrder
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                + New Order
              </button>
            </nav>
          </div>
        )}

        {/* Routes */}
        <Routes>
          {/* Main List View - Default */}
          <Route
            path="/"
            element={
              <OrderHistory
                orders={orders}
                onSelectOrder={(order) => {
                  setSelectedOrder(order);
                  navigate(`/certificate-orders/${order.id}`);
                }}
                selectedOrderId={selectedOrder?.id}
              />
            }
          />

          {/* New Order Form */}
          <Route
            path="/new"
            element={<OrderForm onOrderCreated={handleOrderCreated} />}
          />
          <Route
            path="/new/:propertyId"
            element={<OrderForm onOrderCreated={handleOrderCreated} />}
          />

          {/* Order Detail with Tabs */}
          <Route
            path="/:orderId"
            element={
              currentOrder ? (
                <OrderDetailContainer orderId={orderId || ''}>
                  <OrderTracking order={currentOrder} onUpdate={handleOrderUpdate} />
                </OrderDetailContainer>
              ) : (
                <div className="text-center py-8">Order not found</div>
              )
            }
          />

          {/* Payment Status */}
          <Route
            path="/:orderId/payment"
            element={
              currentOrder ? (
                <OrderDetailContainer orderId={orderId || ''}>
                  <PaymentStatus order={currentOrder} />
                </OrderDetailContainer>
              ) : (
                <div className="text-center py-8">Order not found</div>
              )
            }
          />
        </Routes>
      </div>

      <Footer />
    </div>
  );
}

// Container component for order detail pages with sub-tabs
function OrderDetailContainer({ children, orderId }: { children: React.ReactNode; orderId: string }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isTracking = !location.pathname.includes('/payment');
  const isPayment = location.pathname.includes('/payment');

  return (
    <div>
      {/* Sub-tabs for order detail */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-6 sm:gap-8">
          <button
            onClick={() => navigate(`/certificate-orders/${orderId}`)}
            className={`py-3 px-2 font-medium text-sm whitespace-nowrap transition border-b-2 ${
              isTracking
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            📍 Order Status
          </button>
          <button
            onClick={() => navigate(`/certificate-orders/${orderId}/payment`)}
            className={`py-3 px-2 font-medium text-sm whitespace-nowrap transition border-b-2 ${
              isPayment
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            💳 Payment
          </button>
        </nav>
      </div>

      {/* Content */}
      {children}
    </div>
  );
}

// Export all components for potential standalone use
export { OrderForm, OrderHistory, OrderTracking, PaymentStatus };
